import type { RequestConfig } from '@umijs/max';
import { request } from '@umijs/max';
import { message } from 'antd';
import {
  tokenStorage,
  refreshTokenStorage,
  userStorage,
  emailStorage,
  roleStorage,
  refreshToken as refreshTokenApi,
} from '@/modules/infrastructure/users/services/auth.api';
import { errorConfig } from '@/requestErrorConfig';
import { applyApiBaseToRequestConfig } from '@/apiBase';

const LOGIN_PATH = '/user/login';

/** Clear all auth data and redirect to login */
function clearAuthAndRedirectToLogin() {
  tokenStorage.remove();
  refreshTokenStorage.remove();
  userStorage.remove();
  emailStorage.remove();
  roleStorage.remove();
  message.error('Session expired, please log in again');
  if (window.location.pathname !== LOGIN_PATH) {
    window.location.href = LOGIN_PATH;
  }
}

/** Build retry request options from axios error config */
function buildRetryOptions(config: any) {
  const opts: any = {
    method: config.method,
    skipErrorHandler: true,
    _retry: true,
  };
  if (config.data !== undefined) opts.data = config.data;
  if (config.params !== undefined) opts.params = config.params;
  if (config.headers) opts.headers = { ...config.headers };
  return opts;
}

// Error handling function
const errorHandler = async (error: any, opts?: any) => {
  if (opts?.skipErrorHandler) throw error;

  const { response, config } = error;

  if (response && response.status === 401) {
    // Already retried once, or no refresh token — clear and redirect
    if (config?._retry) {
      clearAuthAndRedirectToLogin();
      throw error;
    }

    const refresh = refreshTokenStorage.get();
    if (!refresh) {
      clearAuthAndRedirectToLogin();
      throw error;
    }

    try {
      const res = await refreshTokenApi({ refresh_token: refresh }, { skipErrorHandler: true });
      const data = res?.data as { access_token?: string; refresh_token?: string } | undefined;
      if (data?.access_token) {
        tokenStorage.set(data.access_token);
        if (data.refresh_token) refreshTokenStorage.set(data.refresh_token);
        // Retry original request with new token (request interceptor will add it)
        const retryOptions = buildRetryOptions(config);
        return request(config.url, retryOptions);
      }
    } catch (refreshErr) {
      // Refresh failed — clear auth and redirect to login
      clearAuthAndRedirectToLogin();
      throw refreshErr;
    }

    clearAuthAndRedirectToLogin();
    throw error;
  }

  // Delegate other errors to the default error handler (BizError, etc.)
  const defaultHandler = errorConfig?.errorConfig?.errorHandler;
  if (defaultHandler) {
    defaultHandler(error, opts);
  } else {
    if (response && response.status) {
      const { status, data } = response;
      if (status === 403) message.error('No permission to access this resource');
      else if (status === 404) message.error('Requested resource not found');
      else if (status >= 500) message.error('Server internal error');
      else message.error(data?.detail || data?.message || `Request error ${status}`);
    } else {
      message.error('Network error, please check your network connection');
    }
  }

  throw error;
};

// Request configuration
export const requestConfig: RequestConfig = {
  // Base URL configuration (optional if you want to set a default)
  // baseURL: 'http://localhost:8000',

  timeout: 10000,

  // Request interceptor
  requestInterceptors: [
    (config: any) => {
      applyApiBaseToRequestConfig(config);

      // Add authentication header if token exists (skip for refresh endpoint — it uses refresh_token in body)
      const isRefreshRequest =
        typeof config.url === 'string' && config.url.includes('/auth/refresh');
      if (!isRefreshRequest) {
        const token = tokenStorage.get();
        if (token) {
          config.headers = {
            ...config.headers,
            Authorization: `Bearer ${token}`,
          };
        }
      }

      // Ensure Content-Type is set for POST requests
      if (config.method?.toLowerCase() === 'post' && !config.headers['Content-Type']) {
        config.headers['Content-Type'] = 'application/json';
      }

      return config;
    },
  ],

  // Response interceptor
  responseInterceptors: [
    (response) => {
      return response;
    },
  ],

  // Error handler
  errorConfig: {
    errorHandler,
  },
};
